import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Valor } from '../../components/Valor';
import { api, type Insights } from '../../lib/api';

/**
 * A leitura do mes.
 *
 * O painel sempre respondeu "quanto". Isto responde "e dai?" — e e a
 * pergunta que faz alguem abrir um sistema de financas duas vezes por semana
 * em vez de duas vezes por ano.
 *
 * A divisao de trabalho aparece na propria tela, e nao so no codigo: o numero
 * grande e do sistema, apurado em centavos inteiros; a frase embaixo e da IA,
 * e ela nao escreve numero nenhum — o servidor descarta qualquer frase com
 * digito antes de mandar para ca. Assim ninguem precisa decidir, olhando,
 * se aquele valor foi calculado ou escrito.
 *
 * Sem IA configurada, ou com o limite da assinatura atingido, os fatos
 * continuam aqui inteiros. So o comentario some.
 *
 * Cada quadro abre. Um numero que nao se abre so pode ser aceito ou ignorado:
 * quem quer AGIR sobre ele precisa ver as linhas que o formaram, e a lista que
 * abre nao e um filtro parecido montado pela tela — sao os lancamentos que o
 * servidor usou para chegar naquele valor, apurados na mesma passada.
 */
export function LeituraDoMes({ month }: { month: string }) {
  const cliente = useQueryClient();
  const [aberto, setAberto] = useState<string | null>(null);

  const leitura = useQuery({
    queryKey: ['insights', month],
    queryFn: () => api.insights(month),
    // A leitura e guardada por horas no servidor; refazer a cada minuto, como
    // o resto da tela faz, so gastaria chamada de modelo para obter o mesmo.
    refetchInterval: false,
    staleTime: 10 * 60 * 1000,
  });

  const atualizar = async () => {
    const nova = await api.insights(month, true);
    cliente.setQueryData(['insights', month], nova);
    setAberto(null);
  };

  if (leitura.isLoading) {
    return (
      <section className="card">
        <h2 className="card-title">Leitura do mes</h2>
        <p className="card-hint" style={{ marginBottom: 0 }}>
          Apurando...
        </p>
      </section>
    );
  }

  const dados = leitura.data;
  if (!dados || dados.facts.length === 0) return null;

  const frases = new Map(dados.reading.map((item) => [item.factId, item.texto]));

  return (
    <section className="card leitura">
      <div className="between">
        <h2 className="card-title">Leitura do mes</h2>
        <button className="btn ghost" onClick={() => void atualizar()}>
          Atualizar
        </button>
      </div>

      <p className="card-hint">
        Os numeros sao do sistema. O comentario e da IA, que le os numeros e nao escreve nenhum.
        Clique num quadro para ver os lancamentos por tras dele.
      </p>

      <div className="leitura-grade">
        {dados.facts.map((fato) => (
          <button
            key={fato.id}
            type="button"
            className={`leitura-item${aberto === fato.id ? ' aberto' : ''}`}
            aria-expanded={aberto === fato.id}
            onClick={() => setAberto(aberto === fato.id ? null : fato.id)}
          >
            <span className="leitura-titulo">{fato.titulo}</span>
            <Valor
              cents={fato.valorCents}
              saida={fato.tipo !== 'RESULTADO'}
              className="leitura-valor"
            />
            <span className="leitura-sobre">{apoio(fato)}</span>
            {frases.get(fato.id) && <p className="leitura-frase">{frases.get(fato.id)}</p>}
            <span className="leitura-abrir">
              {aberto === fato.id ? 'Fechar' : 'Ver os lancamentos'}
            </span>
          </button>
        ))}
      </div>

      {aberto && <Detalhe fato={aberto} month={month} aoFechar={() => setAberto(null)} />}

      {!dados.aiAvailable && dados.aiUnavailableReason && (
        <p className="card-hint" style={{ marginBottom: 0 }}>
          {dados.aiUnavailableReason}
        </p>
      )}
    </section>
  );
}

/**
 * Os lancamentos por tras de um quadro.
 *
 * Os totais vem do servidor, e nao sao somados aqui. E de proposito: a pessoa
 * abriu para conferir um numero, e conferir contra uma soma feita na tela
 * responderia outra pergunta — se a tela sabe somar, e nao se o quadro esta
 * certo.
 *
 * Em dois casos a soma NAO bate com o quadro, e nesses a explicacao do
 * servidor diz por que: "subiu" mostra a diferenca contra o mes passado, e
 * "cobranca repetida" mostra o valor de uma das cobrancas.
 */
function Detalhe({ fato, month, aoFechar }: { fato: string; month: string; aoFechar: () => void }) {
  const detalhe = useQuery({
    queryKey: ['insights-detalhe', month, fato],
    queryFn: () => api.insightsDetail(fato, month),
    refetchInterval: false,
  });

  if (detalhe.isLoading) {
    return (
      <div className="leitura-detalhe">
        <p className="card-hint" style={{ marginBottom: 0 }}>
          Buscando os lancamentos...
        </p>
      </div>
    );
  }

  const dados = detalhe.data;
  if (!dados) return null;

  return (
    <div className="leitura-detalhe">
      <div className="between">
        <h3 className="leitura-detalhe-titulo">{dados.titulo}</h3>
        <button className="btn ghost" onClick={aoFechar}>
          Fechar
        </button>
      </div>

      <p className="card-hint">{dados.explicacao}</p>

      <ul className="leitura-linhas">
        {dados.transactions.map((linha) => (
          <li key={linha.id} className="leitura-linha">
            <span className="leitura-linha-dia">{diaEMes(linha.date)}</span>
            <span className="leitura-linha-nome">
              {/*
                O nome legivel, e a descricao crua embaixo so quando os dois
                diferem: quem esta conferindo precisa reconhecer o lancamento
                no extrato do banco, e o extrato usa o texto cru.
              */}
              <Link
                to={`/lancamentos?search=${encodeURIComponent(linha.description)}`}
                title="Abrir em Lancamentos para ajustar"
              >
                {linha.displayName || linha.description}
              </Link>
              <small>
                {linha.accountName}
                {linha.categoryPath ? ` · ${linha.categoryPath}` : ' · sem categoria'}
              </small>
            </span>
            <Valor cents={linha.amountCents} className="leitura-linha-valor" />
          </li>
        ))}
      </ul>

      <div className="leitura-somas">
        <span>
          {dados.totais.quantidade === 1
            ? '1 lancamento'
            : `${dados.totais.quantidade} lancamentos`}
        </span>
        {dados.totais.entradasCents !== '0' && (
          <span>
            Entrou <Valor cents={dados.totais.entradasCents} />
          </span>
        )}
        {dados.totais.saidasCents !== '0' && (
          <span>
            Saiu <Valor cents={dados.totais.saidasCents} saida />
          </span>
        )}
      </div>
    </div>
  );
}

/** "2026-09-24" vira "24/09". O ano ja esta no titulo do mes. */
function diaEMes(data: string): string {
  return `${data.slice(8, 10)}/${data.slice(5, 7)}`;
}

/**
 * A linha de contexto do fato, montada aqui.
 *
 * Sao os mesmos campos para todos os fatos — sobre o que, quantas vezes, que
 * proporcao —, e cada tipo usa os que fazem sentido. Montar no servidor
 * exigiria mandar frase pronta junto com o numero, e ai a tela nao saberia
 * mais formatar dinheiro do seu jeito.
 */
function apoio(fato: Insights['facts'][number]): string {
  const partes: string[] = [];
  if (fato.sobre) partes.push(fato.sobre);

  if (fato.tipo === 'MAIOR_CATEGORIA' && fato.percentual !== null) {
    partes.push(`${fato.percentual.toFixed(1).replace('.', ',')}% das saidas`);
  }
  if (fato.tipo === 'SUBIU' && fato.percentual !== null) {
    partes.push(`${fato.percentual.toFixed(1).replace('.', ',')}% a mais`);
  }
  if (fato.tipo === 'RESULTADO' && fato.percentual !== null) {
    partes.push(`${fato.percentual.toFixed(1).replace('.', ',')}% do que entrou`);
  }
  if (fato.tipo === 'ASSINATURAS' && fato.contagem !== null) {
    partes.push(fato.contagem === 1 ? '1 cobranca mensal' : `${fato.contagem} cobrancas mensais`);
  }
  if (fato.tipo === 'REPETIDO' && fato.contagem !== null) {
    partes.push(`${fato.contagem} vezes em poucos dias`);
  }

  return partes.join(' · ');
}
